import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Wrench, Users, Car, Package, ShoppingCart, Store, Settings, LogOut, UserCog } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/work-orders", label: "Repair Orders", icon: Wrench, testid: "nav-work-orders" },
  { to: "/parts-to-order", label: "Parts to Order", icon: ShoppingCart, testid: "nav-parts-to-order" },
  { to: "/customers", label: "Vehicle Register", icon: Users, testid: "nav-customers" },
  { to: "/dealers", label: "Dealers", icon: Store, testid: "nav-dealers" },
  { to: "/vehicles", label: "Vehicles", icon: Car, testid: "nav-vehicles" },
  { to: "/parts", label: "Parts", icon: Package, testid: "nav-parts" },
  { to: "/users", label: "Users", icon: UserCog, testid: "nav-users" },
];

export default function Layout() {
  const location = useLocation();
  const active = NAV.find((n) => location.pathname.startsWith(n.to));
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-page flex" data-testid="app-shell">
      {/* Sidebar */}
      <aside
        className="hidden md:flex w-64 flex-col bg-sidebar border-r border-warm sticky top-0 h-screen"
        data-testid="sidebar"
      >
        <div className="px-4 py-6 border-b border-warm">
          <div className="bg-white rounded-2xl shadow-card border border-warm p-3 flex items-center justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_dealer-stock-1/artifacts/o3em795n_WhatsApp%20Image%202026-07-02%20at%202.28.23%20PM.jpeg"
              alt="Car Dream — Auto · Repair · Paint"
              className="w-full max-w-[160px] h-auto object-contain"
              data-testid="brand-logo"
            />
          </div>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-white text-[#1A1A1A] shadow-card border-l-4 border-[#E5A82E]"
                    : "text-[#63677A] hover:bg-hover hover:text-[#2B2D42]"
                }`
              }
            >
              <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-warm space-y-2">
          {user && (
            <div className="px-3 py-2 rounded-lg bg-white/60 border border-warm" data-testid="sidebar-user">
              <div className="text-xs font-medium text-[#2B2D42] truncate">{user.name || "Admin"}</div>
              <div className="text-[11px] text-slate-muted truncate">@{user.username}</div>
            </div>
          )}
          <button
            type="button"
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[#63677A] hover:bg-hover hover:text-[#2B2D42] transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
          <div className="flex items-center gap-3 px-3 py-2 text-xs text-slate-muted">
            <Settings className="w-4 h-4" />
            <span>v1.0 · Front-desk mode</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile top nav */}
        <div className="md:hidden bg-sidebar border-b border-warm px-4 py-3 flex items-center justify-between gap-3 sticky top-0 z-10">
          <img
            src="https://customer-assets.emergentagent.com/job_dealer-stock-1/artifacts/o3em795n_WhatsApp%20Image%202026-07-02%20at%202.28.23%20PM.jpeg"
            alt="Car Dream"
            className="h-8 w-auto object-contain"
            data-testid="brand-logo-mobile"
          />
          <button
            type="button"
            onClick={logout}
            className="flex items-center gap-1.5 text-xs font-medium text-[#63677A] hover:text-[#2B2D42] px-2 py-1"
            data-testid="logout-button-mobile"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
        <div className="md:hidden bg-sidebar border-b border-warm overflow-x-auto no-scrollbar">
          <div className="flex gap-1 px-2 py-2 min-w-max">
            {NAV.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`${testid}-mobile`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                    isActive
                      ? "bg-white text-[#1A1A1A] border-b-2 border-[#E5A82E] shadow-card"
                      : "text-[#63677A]"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {label}
              </NavLink>
            ))}
          </div>
        </div>

        <div
          className="p-6 md:p-8 max-w-[1400px] mx-auto animate-in fade-in duration-500"
          data-testid={`page-${active?.to?.replace("/", "") || "root"}`}
        >
          <Outlet />
        </div>
      </main>
    </div>
  );
}
